// pages/training/constants.tsx
//
// Shared display vocabulary for the learner-facing training screens.

import React from 'react';
import {
  PlayCircle,
  FilePdf,
  LinkSimple,
  ListChecks,
  Question,
  Article,
  Presentation,
} from '@phosphor-icons/react';
import type {
  MyTrainingRow,
  TrainingContentType,
  TrainingCredentialType,
  TrainingProgressStatus,
} from '../../types/training';

export const contentTypeIcons: Record<TrainingContentType, React.ReactNode> = {
  video: <PlayCircle />,
  pdf: <FilePdf />,
  link: <LinkSimple />,
  checklist: <ListChecks />,
  quiz: <Question />,
  article: <Article />,
  scorm: <PlayCircle />,
  presentation: <Presentation />,
};

export const contentTypeLabels: Record<TrainingContentType, string> = {
  video: 'Video',
  pdf: 'PDF',
  link: 'Link',
  checklist: 'Checklist',
  quiz: 'Quiz',
  article: 'Article',
  scorm: 'SCORM',
  presentation: 'Presentation',
};

/** '' is ordinary training — nothing to badge. */
export const credentialTypeLabels: Record<TrainingCredentialType, string> = {
  '': '',
  osha_10: 'OSHA 10',
  osha_30: 'OSHA 30',
  fall_protection: 'Fall Protection',
  ladder_safety: 'Ladder Safety',
  driver_training: 'Driver Training',
  first_aid_cpr: 'First Aid / CPR',
  hazard_communication: 'Hazard Communication',
  heat_illness: 'Heat Illness Prevention',
  equipment: 'Equipment / Tool Certification',
  other: 'Credential',
};

export const statusLabels: Record<TrainingProgressStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  completed: 'Completed',
  failed: 'Not passed',
};

export const statusColors: Record<TrainingProgressStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  not_started: 'neutral',
  in_progress: 'warning',
  completed: 'success',
  failed: 'danger',
};

const EXPIRING_SOON_DAYS = 30;
const DUE_SOON_DAYS = 7;

const daysFromNow = (iso: string): number =>
  Math.floor((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));

const inDays = (days: number, verb: string): string =>
  days === 0 ? `${verb} today` : `${verb} in ${days} day${days === 1 ? '' : 's'}`;

/**
 * The one deadline that matters for a row, as a chip.
 *
 * A lapsed certification outranks everything: fall protection is the number
 * one OSHA citation in roofing, and an expired card is a person who should
 * not be on a roof. Recertification resets the row to not_started on its own
 * schedule, so until that beat job runs we say "Expired" over "Completed".
 */
export const getDeadlineChip = (
  row: Pick<MyTrainingRow, 'status' | 'due_date' | 'is_overdue' | 'expires_at'>
): { color: 'danger' | 'warning' | 'success' | 'neutral'; label: string } | null => {
  if (row.expires_at) {
    const daysLeft = daysFromNow(row.expires_at);
    if (daysLeft < 0) {
      return { color: 'danger', label: `Expired ${new Date(row.expires_at).toLocaleDateString()}` };
    }
    if (row.status === 'completed' && daysLeft <= EXPIRING_SOON_DAYS) {
      return { color: 'warning', label: inDays(daysLeft, 'Expires') };
    }
  }

  if (row.status === 'completed') {
    return { color: 'success', label: 'Completed' };
  }
  if (row.is_overdue) {
    return { color: 'danger', label: 'Overdue' };
  }
  if (row.due_date) {
    const daysLeft = daysFromNow(row.due_date);
    return daysLeft <= DUE_SOON_DAYS
      ? { color: 'warning', label: inDays(daysLeft, 'Due') }
      : { color: 'neutral', label: `Due ${new Date(row.due_date).toLocaleDateString()}` };
  }
  return null;
};

/** Wording for the button that opens an item, given where the learner left off. */
export const getActionLabel = (row: Pick<MyTrainingRow, 'status'>): string => {
  switch (row.status) {
    case 'in_progress':
      return 'Continue';
    case 'completed':
      return 'Review';
    case 'failed':
      return 'Try again';
    default:
      return 'Start';
  }
};
